import React, { useEffect, useState } from 'react';
import { X, Copy, Smartphone, ScanLine, MessageSquare, Gift, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_CONFIG } from '../../config/api.config';

interface ProUpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** site áp dụng dùng thử: 'ebook' (mặc định, gồm pmaker) */
    site?: 'ebook' | 'eng';
}

const authHeaders = () => {
    const t = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
    return { Authorization: `Bearer ${t}` };
};

const ProUpgradeModal: React.FC<ProUpgradeModalProps> = ({ isOpen, onClose, site = 'ebook' }) => {
    const { user } = useAuth();
    const base = API_CONFIG.python.apiUrl;
    const [eligible, setEligible] = useState(false);
    const [trialDays, setTrialDays] = useState(3);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // Khi mở modal: hỏi tài khoản còn được dùng thử không (mỗi tài khoản 1 lần/site).
    useEffect(() => {
        if (!isOpen || !user) return;
        let alive = true;
        fetch(`${base}/membership/trial-eligibility?site=${site}`, { headers: authHeaders() })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive && d) { setEligible(!!d.eligible); setTrialDays(d.days || 3); } })
            .catch(() => { });
        return () => { alive = false; };
    }, [isOpen, user, site, base]);

    const startTrial = async () => {
        setBusy(true); setErr('');
        try {
            const r = await fetch(`${base}/membership/start-trial`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ site }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d?.detail || 'Không kích hoạt được dùng thử');
            alert(`Đã kích hoạt dùng thử ${d.days || trialDays} ngày! Trang sẽ tải lại để mở khóa tính năng.`);
            window.location.reload();
        } catch (e) {
            setErr((e as Error).message || 'Có lỗi xảy ra');
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
    };

    const transferContent = user ? `ebookvatli365_${user.email}` : 'ebookvatli365_your-email';

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden relative animate-in fade-in zoom-in duration-300">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
                >
                    <X className="w-6 h-6 text-gray-500" />
                </button>

                {/* Dùng thử miễn phí — hiện khi tài khoản chưa từng dùng thử & chưa PRO */}
                {eligible && (
                    <div className="border-b border-blue-100 bg-gradient-to-r from-blue-50 to-emerald-50 p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex items-start gap-3 flex-1">
                                <span className="p-2 bg-emerald-100 text-emerald-600 rounded-xl"><Gift className="w-5 h-5" /></span>
                                <div>
                                    <p className="font-black text-gray-900">Dùng thử miễn phí {trialDays} ngày</p>
                                    <p className="text-xs text-gray-500">Mở khóa toàn bộ tính năng PRO ngay — không cần thanh toán. Mỗi tài khoản chỉ dùng thử một lần.</p>
                                </div>
                            </div>
                            <button
                                onClick={startTrial}
                                disabled={busy}
                                className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 text-white rounded-xl font-black hover:bg-emerald-700 transition shadow-lg shadow-emerald-200 disabled:opacity-60"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                                Kích hoạt dùng thử
                            </button>
                        </div>
                        {err && <p className="mt-2 text-xs font-bold text-rose-600">{err}</p>}
                        <p className="mt-3 text-center text-[11px] font-bold uppercase tracking-widest text-gray-400">— hoặc nâng cấp PRO trọn gói —</p>
                    </div>
                )}

                <div className="flex flex-col md:flex-row">
                    {/* QR Section */}
                    <div className="md:w-1/2 bg-gray-50 p-8 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-gray-100">
                        <div className="bg-white p-4 rounded-2xl shadow-lg mb-4">
                            <img 
                                src="/qr-sacombank.jpg" 
                                alt="Payment QR" 
                                className="w-full aspect-square object-contain"
                            />
                        </div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                            <ScanLine className="w-4 h-4" /> Quét mã để thanh toán
                        </p>
                    </div>

                    {/* Info Section */}
                    <div className="md:w-1/2 p-8">
                        <h3 className="text-2xl font-black text-gray-900 mb-2">Nâng cấp <span className="text-blue-600">Ebook PRO</span></h3>
                        <p className="text-gray-500 text-sm mb-6">Mở khóa toàn bộ bài học, trắc nghiệm và tính năng AI cao cấp.</p>

                        <div className="space-y-4 mb-6">
                            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs font-bold text-blue-600 uppercase">Số tài khoản</span>
                                    <button onClick={() => copyToClipboard('040062507464')} className="text-blue-400 hover:text-blue-600">
                                        <Copy size={14} />
                                    </button>
                                </div>
                                <p className="font-mono font-bold text-lg text-gray-900">040062507464</p>
                                <p className="text-xs text-gray-500">Sacombank - TRAN QUOC QUAN</p>
                            </div>

                            <div className="bg-green-50/50 p-4 rounded-xl border border-green-100">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs font-bold text-green-600 uppercase">Nội dung chuyển khoản</span>
                                    <button onClick={() => copyToClipboard(transferContent)} className="text-green-400 hover:text-green-600">
                                        <Copy size={14} />
                                    </button>
                                </div>
                                <p className="font-mono font-bold text-gray-900 break-all">{transferContent}</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-start gap-3 text-sm">
                                <div className="p-1.5 bg-orange-100 rounded-lg text-orange-600 mt-0.5">
                                    <Smartphone className="w-4 h-4" />
                                </div>
                                <p className="text-gray-600">
                                    Sau khi chuyển khoản, vui lòng gửi ảnh minh chứng qua Zalo: <span className="font-bold text-gray-900">0978531191</span>
                                </p>
                            </div>
                            <div className="flex items-start gap-3 text-sm">
                                <div className="p-1.5 bg-blue-100 rounded-lg text-blue-600 mt-0.5">
                                    <MessageSquare className="w-4 h-4" />
                                </div>
                                <p className="text-gray-600">
                                    Hệ thống sẽ kích hoạt tài khoản của bạn trong vòng 5-10 phút.
                                </p>
                            </div>
                        </div>

                        <button 
                            onClick={() => window.open(`https://zalo.me/0978531191`, '_blank')}
                            className="w-full mt-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                        >
                            Gửi minh chứng qua Zalo
                        </button>
                        <a href="/ho-so" className="block text-center w-full mt-3 py-2.5 border-2 border-blue-200 text-blue-700 rounded-xl font-bold hover:bg-blue-50">
                            Quản lý / kích hoạt gói tại trang Hồ sơ →
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProUpgradeModal;
